import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as fromActions from './user-management-actions';
import {exhaustMap, map, mergeMap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {CreateUserResponseModel} from '@core/modules/rest/user/response/create-user-response.model';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';

@Injectable()
export class UserManagementEffects {

    constructor(private actions$: Actions, private userRestService: UserRestService, private consentRestService: ConsentRestService) {
    }

    usersByOrganization$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(fromActions.GetUsersByOrganization),
                exhaustMap((action: ReturnType<typeof fromActions.GetUsersByOrganization>) => {
                    return this.userRestService.getUsersByOrganization(action.organization, action.offset)
                        .pipe(
                            map((response: UserDataModel[]) =>
                                fromActions.GetUsersByOrganizationSuccess({
                                    users: response
                                }))
                        );
                })
            );
    });

    filteredUsersByOrganization$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(fromActions.GetFilteredUsersByOrganization),
                exhaustMap((action: ReturnType<typeof fromActions.GetFilteredUsersByOrganization>) => {
                    return this.userRestService.getFilteredUsersByOrganization(action.organization, action.searchData)
                        .pipe(
                            map((response: UserDataModel[]) => {
                                    return fromActions.GetFilteredUsersByOrganizationSuccess({
                                        users: response
                                    });
                                }
                            )
                        );
                })
            );
    });

    createUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.CreateUser),
            exhaustMap((action: ReturnType<typeof fromActions.CreateUser>) => {
                return this.userRestService.createUser(action.request)
                    .pipe(
                        mergeMap((response: CreateUserResponseModel) => {
                            action.executed(response.created, response.status, response.userData, response.emailInUse);
                            return response.created
                                ? [fromActions.CreateUserSuccess({newUser: response.userData})]
                                : [];
                        })
                    );
            })
        ));

    alterUserStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.AlterUserStatus),
            exhaustMap((action: ReturnType<typeof fromActions.AlterUserStatus>) => {
                return this.userRestService.alterUserActiveStatus(action.request)
                    .pipe(
                        map((alteredUser: UserDataModel) => fromActions.AlterUserStatusSuccess({
                            alteredUser
                        }))
                    );
            })
        ));

    deleteUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.DeleteUser),
            exhaustMap((action: ReturnType<typeof fromActions.DeleteUser>) => {
                return this.userRestService.deleteUser(action.userId)
                    .pipe(
                        map(() => fromActions.DeleteUserSuccess({
                            userId: action.userId
                        }))
                    );
            })
        ));

    updateUserRoles$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.UpdateUserRoles),
            exhaustMap((action: ReturnType<typeof fromActions.UpdateUserRoles>) => {
                return this.userRestService.saveRoles(action.request)
                    .pipe(
                        map(() => fromActions.UpdateUserRolesSuccess({
                            userId: action.request.userId,
                            newRoles: action.request.roles
                        }))
                    );
            })
        ));

    educationUserData$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(fromActions.SaveUserEducationSettings),
                exhaustMap((action: ReturnType<typeof fromActions.SaveUserEducationSettings>) => {
                    return this.userRestService.saveEducationSettings(action.request)
                        .pipe(
                            mergeMap((response: UserDataModel) => {
                                action.executed();
                                return [fromActions.SaveUserEducationSettingsSuccess({
                                    updatedUser: response
                                })];
                            })
                        );
                }));
    });

    getUsersQuestionNumberOnActiveConsent$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.GetStartedConsentWithQuestions),
            exhaustMap((action: ReturnType<typeof fromActions.GetStartedConsentWithQuestions>) => {
                return this.consentRestService.getUsersNoOfQuestionsOnActiveConsent(action.userId)
                    .pipe(
                        map((noOfQuestions: number) => {
                            action.executed(noOfQuestions);
                            return fromActions.GetStartedConsentWithQuestionsSuccess();
                        })
                    );
            })
        )
    );

    mergeUsersActiveConsentComments$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.MergeUsersActiveConsentComments),
            exhaustMap((action: ReturnType<typeof fromActions.MergeUsersActiveConsentComments>) => {
                return this.consentRestService.mergeUsersActiveConsentComments(action.userId)
                    .pipe(
                        map(() => {
                            return fromActions.MergeUsersActiveConsentCommentsSuccess();
                        })
                    );
            })
        )
    );
}

